import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus';
import { SocialMediaNetworkService } from '@/scripts/services/social-media/social-media-network-service';
import { SocialMediaConnectService } from '@/scripts/services/social-media/social-media-connect-service';

export const SocialMediaBoardServiceMixin = {
    data() {
        return {
            deletingItems: [],
        };
    },

    methods: {
        refreshEvent() {
            EventBus.$emit('social-media-manage:update');
        },

        asSocialMediaItem(item) {
            return {
                id: item.Id,
                name: item.Name,
                accountType: item.AccountType,
                accountsConnected: item.AccountsConnected,
                lastSync: this.$moment.utc(item.LastSync),
            };
        },

        asSocialMediaAccount(item) {
            return {
                id: item.Id,
                socialMediaAccount: {
                    accountName: item.AccountName,
                    social: item.AccountType,
                    profileUrl: item.ExternalProfileUrl,
                    profileImageUrl: item.ProfileImageUrl,
                },
                socialMediaStatus: item.IsActive ? 'enabled' : 'disabled',
                lastSync: this.$moment.utc(item.LastSync),
                socialNetworkType: item.SocialNetworkType,
            };
        },

        openManageSocialMediaWidget(item, clientId) {
            EventBus.$emit('open-widget', {
                name: 'social-media-manage-widget',
                currentContent: 'socialManage',
                item: item,
                clientId: clientId,
            });
        },

        createChangeStatusSocialMediaModal(item, confirmFunction) {
            return () => {
                const actions = {
                    confirm: {
                        label: `${item.socialMediaStatus === 'enabled' ? 'Disable' : 'Enable'}`,
                        action: () => confirmFunction(item),
                    },

                    cancel: {
                        label: 'Cancel',
                    },
                };

                let socialMedia = '';

                switch (item.socialMediaAccount.social) {
                    case 'linkedin':
                        socialMedia = 'LinkedIn';
                        break;
                    case 'facebook':
                        socialMedia = 'Facebook';
                        break;

                    default:
                        socialMedia = 'LinkedIn';
                        break;
                }

                let content = '';

                if (item.socialMediaStatus === 'enabled') {
                    content = `<p>If you disable this account, GlassHive won't be able to access your ${socialMedia} page's data. 
                    You can always enable this account again and pick up right where you left off. Are you sure you want 
                    to disable <b>${item.socialMediaAccount.accountName}</b>?</p>`;
                } else {
                    content = `<p>If you enable this account, GlassHive will be able to access your ${socialMedia} page's data. 
                    Are you sure you want to enable <b>${item.socialMediaAccount.accountName}</b>?</p>`;
                }

                this.$modal.show(
                    AppPopup,
                    {
                        type: 'alert-popup',
                        title: `Are you sure you want to ${item.socialMediaStatus === 'enabled' ? 'disabled' : 'enabled'} this account?`,
                        content,
                        actions,
                        confirmText: 'Delete',
                    },
                    {
                        height: this.$helpers.setPopupHeight(),
                        width: this.$helpers.setPopupWidth(),
                    },
                );
            };
        },

        createDeleteSocialMediaAccountModal(item, confirmFunction) {
            return () => {
                const actions = {
                    confirm: {
                        label: 'Remove',
                        action: () => confirmFunction(item),
                    },

                    cancel: {
                        label: 'Cancel',
                    },
                };

                let socialMedia = '';

                switch (item.socialMediaAccount.social) {
                    case 'linkedin':
                        socialMedia = 'LinkedIn';
                        break;
                    case 'facebook':
                        socialMedia = 'Facebook';
                        break;

                    default:
                        socialMedia = 'LinkedIn';
                        break;
                }

                const content = `<p>By removing this account, GlassHive won't be able to access your ${socialMedia} account. All history and data will be removed from 
                our system. Are you sure you want to remove <b>${item.socialMediaAccount.accountName}</b>?</p>`;

                this.$modal.show(
                    AppPopup,
                    {
                        type: 'alert-popup',
                        title: 'Are you sure you want to remove this account?',
                        content,
                        actions,
                        confirmText: 'Delete',
                    },
                    {
                        height: this.$helpers.setPopupHeight(),
                        width: this.$helpers.setPopupWidth(),
                    },
                );
            };
        },

        getFilterParam(options, socialMedia, onlyActives) {

            const queryFilters = [];

            if (socialMedia) {
                if (options.search) {
                    queryFilters.push(`{"AccountType":"${socialMedia}"${this.getSearchFilter(options.search)}}`);
                } else {
                    if (!onlyActives) {
                        queryFilters.push(`{"AccountType":"${socialMedia}"}`);
                    } else {
                        queryFilters.push(`{"AccountType":"${socialMedia}", "IsActive": ${onlyActives} }`);
                    }
                }
            }

            return queryFilters.length > 0 ? `${queryFilters.join('&')}` : '';
        },

        getSearchFilter(search) {
            const encodedSearch = encodeURIComponent(`%${search}%`);

            return `,"AccountName": {"$like":"${encodedSearch}"}`;
        },

        getPageParam(options) {
            return options.itemsPerPage && options.page ? `offset=${options.itemsPerPage * (options.page - 1)}` : '';
        },

        getLimitParam(options) {
            return options.itemsPerPage ? `limit=${options.itemsPerPage}` : '';
        },

        createParamsFromBoardQuery(socialMedia, options, onlyActives) {
            return {
                filter: this.getFilterParam(options, socialMedia, onlyActives),
                skip: this.getPageParam(options),
                limit: this.getLimitParam(options),
            };
        },

        // Get Social Networks
        getSocialMedias(clientId) {
            return new Promise((resolve, reject) => {
                SocialMediaNetworkService.get(clientId)
                    .then(({ data }) => resolve({
                        items: data.data.map(this.asSocialMediaItem)
                    }))
                    .catch(reject);
            });
        },

        // Get Social Media Accounts
        getSocialMediasAccountsInfo(socialMedia, clientId, options = {}, onlyActives = false) {

            let requestConfig = this.createParamsFromBoardQuery(socialMedia, options, onlyActives);

            return new Promise((resolve, reject) => {
                SocialMediaNetworkService.getAccounts(clientId, requestConfig)
                    .then(({ data }) => resolve({
                        items: data.data.map(this.asSocialMediaAccount),
                        totalItems: data.count,
                    }))
                    .catch(reject);
            });
        },

        // Delete a social media account

        deleteSocialMediaAccount(item, clientId) {
            this.deletingItems = [item];

            return new Promise((resolve, reject) => {
                SocialMediaNetworkService.delete(item.id, clientId)
                    .then(({ data }) => resolve({
                        message: data.message,
                    }))
                    .catch(reject);
            });

        },

        // Change status of Social Media Connection

        putSocialMediaStatus(id, status, clientId) {

            const body = {
                isActive: status,
            };

            return new Promise((resolve, reject) => {
                SocialMediaNetworkService.put(id, clientId, body)
                    .then(({ data }) => resolve({
                        message: data.message,
                        actualStatus: status,
                    }))
                    .catch(reject);
            });
        },

        // Get redirect to social media connection

        getRedirectToSocialMediaConnection(socialMedia, clientId) {
            SocialMediaConnectService.get(clientId, socialMedia)
            .then(({ data }) => {
                window.open(data.data.url, '_self');
            }).catch((error) => {
                this.$helpers.showFailNotification('An error occurred trying to redirect.', { error });
            });
        },
    }

}